import { createAction, props } from '@ngrx/store';
import {
  GenerateCaptchaResponse,
  ILoginResp,
  SessionResponse,
} from '../../models/LoginResponse';
import { AuthState } from './auth.reducer';
import { IResponseObject } from '../../models/common.model';

const login = createAction(
  '[Auth] Login',
  props<{
    username: string;
    password: string;
    intendedUrl?: string;
    captchaInput?: string;
  }>()
);

const loginSuccess = createAction(
  '[Auth] Login Success',
  props<{ data: ILoginResp; intendedUrl: string }>()
);

const loginFailure = createAction(
  '[Auth] Login Failure',
  props<{
    error: string;
    data: { isBlocked: boolean; remainingLockTime: number } | null;
  }>()
);

const isSessionExist = createAction(
  '[Auth] isSessionExist',
  props<{ intendedUrl?: string }>()
);

const isSessionExistSuccess = createAction(
  '[Auth] isSessionExist Success',
  props<{ data: SessionResponse; intendedUrl?: string }>()
);

const isSessionExistFailure = createAction(
  '[Auth] isSessionExist Failure',
  props<{ error: string }>()
);

const logout = createAction('[Auth] logout', props<any>());

const logoutSuccess = createAction('[Auth] logout Success', props<any>());

const logoutFailure = createAction(
  '[Auth] logout Failure',
  props<{
    error: string;
  }>()
);

const restoreState = createAction(
  '[Auth] Restore State',
  props<{ auth: Partial<AuthState> }>()
);

const forgotPassword = createAction(
  '[Auth] Forgot Password',
  props<{ username: string, withRedirect?: boolean }>()
);
const forgotPasswordSuccess = createAction(
  '[Auth] Forgot Password Success',
  props<{ username: string, withRedirect?: boolean }>()
);
const forgotPasswordFailure = createAction(
  '[Auth] Forgot Password Failure',
  props<{ error: string }>()
);

const resetPasswordLink = createAction(
  '[Auth] Reset Password Link',
  props<{ username: string }>()
);

const resetPasswordLinkSuccess = createAction(
  '[Auth] Reset Password Success',
  props<{ username: string }>()
);

const resetPasswordLinkFailure = createAction(
  '[Auth] Reset Password Failure',
  props<{ error: string }>()
);

const generateCaptcha = createAction('[Auth] Generate Captcha');

const generateCaptchaSuccess = createAction(
  '[Auth] Generate Captcha Success',
  props<IResponseObject<GenerateCaptchaResponse>>()
);
const generateCaptchaFailure = createAction(
  '[Auth] Generate Captcha Failure',
  props<{ error: string }>()
);

const useMagicLink = createAction(
  '[Auth] Use Magic Link',
  props<{ email: string }>()
);

const useMagicLinkSuccess = createAction(
  '[Auth] Use Magic Link Success',
  props<{ data: Partial<ILoginResp> }>()
);
const useMagicLinkFailure = createAction(
  '[Auth] Use Magic Link Failure',
  props<{
    error: string;
    data: { isBlocked: boolean; remainingLockTime: number } | null;
  }>()
);

const validateMagicLink = createAction(
  '[Auth] Validate Magic Link',
  props<{ token: string }>()
);

const validateMagicLinkSuccess = createAction(
  '[Auth] Validate Magic Link Success',
  props<{ data: ILoginResp; intendedUrl: string }>()
);
const validateMagicLinkFailure = createAction(
  '[Auth] Validate Magic Link Failure',
  props<{
    error: string;
    data: { isBlocked: boolean; remainingLockTime: number } | null;
  }>()
);
const verifyResetToken = createAction(
  '[Auth] Verify Reset Token',
  props<{ token: string; isResetToken?: boolean }>()
);

const verifyResetTokenSuccess = createAction(
  '[Auth] Verify Reset Token Success',
  props<{ data: string }>()
);
const verifyResetTokenFailure = createAction(
  '[Auth] Verify Reset Token Failure',
  props<{
    error: string;
  }>()
);

const validateMagicOTP = createAction(
  '[Auth] Validate Magic OTP',
  props<{ email: string; otp: string }>()
);

const validateMagicOTPSuccess = createAction(
  '[Auth] Validate Magic OTP Success',
  props<{ data: ILoginResp; intendedUrl: string }>()
);
const validateMagicOTPFailure = createAction(
  '[Auth] Validate Magic OTP Failure',
  props<{
    error: string;
    data: { isBlocked: boolean; remainingLockTime: number } | null;
  }>()
);

const validateMFAOTP = createAction(
  '[Auth] Validate MFA OTP',
  props<{ email: string; otp: string }>()
);

const validateMFAOTPSuccess = createAction(
  '[Auth] Validate MFA OTP Success',
  props<{ data: ILoginResp; intendedUrl: string }>()
);
const validateMFAOTPFailure = createAction(
  '[Auth] Validate MFA OTP Failure',
  props<{
    error: string;
    data: { isBlocked: boolean; remainingLockTime: number } | null;
  }>()
);

const resetPassword = createAction(
  '[Auth] Reset Password',
  props<{ token: string; newPassword: string; confirmPassword: string }>()
);
const resetPasswordSuccess = createAction(
  '[Auth] Reset Password Success',
  props<{ data: {} }>()
);
const resetPasswordFailure = createAction(
  '[Auth] Reset Password Failure',
  props<{ error: string }>()
);
const changePassword = createAction(
  '[Auth] Change Password',
  props<{ oldPassword: string; newPassword: string }>()
);
const changePasswordSuccess = createAction(
  '[Auth] Change Password Success',
  props<{ data: {} }>()
);
const changePasswordFailure = createAction(
  '[Auth] Change Password Failure',
  props<{ error: string }>()
);

const resendMFAOTP = createAction(
  '[Auth] Resend MFA OTP',
  props<{ email: string }>()
);

const resendMFAOTPSuccess = createAction(
  '[Auth] Resend MFA OTP Success',
);

const resendMFAOTPFailure = createAction(
  '[Auth] Resend MFA OTP Failure',
  props<{ error: string,  data: { isBlocked: boolean; remainingLockTime: number } | null; }>()
);

const AuthActions = {
  login,
  loginSuccess,
  loginFailure,
  isSessionExist,
  isSessionExistSuccess,
  isSessionExistFailure,
  logout,
  logoutSuccess,
  logoutFailure,
  restoreState,
  forgotPassword,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordLink,
  resetPasswordLinkSuccess,
  resetPasswordLinkFailure,
  generateCaptcha,
  generateCaptchaSuccess,
  generateCaptchaFailure,
  useMagicLink,
  useMagicLinkSuccess,
  useMagicLinkFailure,
  validateMagicLink,
  validateMagicLinkSuccess,
  validateMagicLinkFailure,
  validateMagicOTP,
  validateMagicOTPSuccess,
  validateMagicOTPFailure,
  validateMFAOTP,
  validateMFAOTPSuccess,
  validateMFAOTPFailure,
  resetPassword,
  resetPasswordSuccess,
  resetPasswordFailure,
  changePassword,
  changePasswordSuccess,
  changePasswordFailure,
  verifyResetToken,
  verifyResetTokenSuccess,
  verifyResetTokenFailure,
  resendMFAOTP,
  resendMFAOTPSuccess,
  resendMFAOTPFailure,
};

export { AuthActions };
